/**
 * Card Status Service
 * Håndterer status tracking for kort i Firebase Firestore
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from '../config/firebase'

// Status værdier
export const CARD_STATUS = {
  CREATED: 'created',                    // Kort er lavet, sender har ikke svaret endnu
  SENDER_RESPONDED: 'sender_responded', // Sender har svaret
  OPENED: 'opened',                     // Modtager har åbnet linket
  RECIPIENT_RESPONDED: 'recipient_responded', // Modtager har svaret
  READY_FOR_FEEDBACK: 'ready_for_feedback'    // Klar til at se feedback
}

/**
 * Opret kort med status i Firebase
 */
export async function createCardWithStatus(preferences, booking, shortCode) {
  try {
    const cardRef = doc(collection(db, 'cards'), shortCode)
    
    const cardData = {
      shortCode,
      preferences,
      booking: booking || null,
      status: CARD_STATUS.CREATED,
      senderResponses: null,
      recipientResponses: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    
    await setDoc(cardRef, cardData)
    console.log('✅ Card created with status:', shortCode)
    return { success: true, shortCode }
  } catch (error) {
    console.error('❌ Error creating card:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Opdater kort status
 */
export async function updateCardStatus(shortCode, status, data = {}) {
  try {
    const cardRef = doc(db, 'cards', shortCode)
    
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
      ...data
    }
    
    await updateDoc(cardRef, updateData)
    console.log(`✅ Card status updated: ${shortCode} → ${status}`)
    return { success: true }
  } catch (error) {
    console.error('❌ Error updating card status:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Hent kort status
 */
export async function getCardStatus(shortCode) {
  try {
    const cardRef = doc(db, 'cards', shortCode)
    const cardDoc = await getDoc(cardRef)
    
    if (!cardDoc.exists()) {
      return { success: false, error: 'Card not found' }
    }
    
    const data = cardDoc.data()
    return { 
      success: true, 
      data: {
        ...data,
        id: cardDoc.id
      }
    }
  } catch (error) {
    console.error('❌ Error getting card status:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Lyt til kort status ændringer (real-time)
 */
export function listenToCardStatus(shortCode, callback) {
  try {
    const cardRef = doc(db, 'cards', shortCode)
    
    const unsubscribe = onSnapshot(cardRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data()
        callback({
          success: true,
          data: {
            ...data,
            id: snapshot.id
          }
        })
      } else {
        callback({ success: false, error: 'Card not found' })
      }
    }, (error) => {
      console.error('❌ Error listening to card status:', error)
      callback({ success: false, error: error.message })
    })
    
    return unsubscribe
  } catch (error) {
    console.error('❌ Error setting up listener:', error)
    return null
  }
}

/**
 * Gem sender svar
 */
export async function saveSenderResponses(shortCode, responses, bookingResponse = null, alternativeBooking = null) {
  try {
    const cardRef = doc(db, 'cards', shortCode)
    
    await updateDoc(cardRef, {
      senderResponses: responses,
      bookingResponse: bookingResponse,
      alternativeBooking: alternativeBooking,
      status: CARD_STATUS.SENDER_RESPONDED,
      updatedAt: serverTimestamp()
    })
    
    console.log('✅ Sender responses saved:', shortCode)
    return { success: true }
  } catch (error) {
    console.error('❌ Error saving sender responses:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Gem modtager svar
 */
export async function saveRecipientResponses(shortCode, responses, bookingResponse = null, alternativeBooking = null) {
  try {
    const cardRef = doc(db, 'cards', shortCode)
    
    await updateDoc(cardRef, {
      recipientResponses: responses,
      recipientBookingResponse: bookingResponse,
      recipientAlternativeBooking: alternativeBooking,
      status: CARD_STATUS.READY_FOR_FEEDBACK,
      updatedAt: serverTimestamp()
    })
    
    console.log('✅ Recipient responses saved:', shortCode)
    return { success: true }
  } catch (error) {
    console.error('❌ Error saving recipient responses:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Marker kort som åbnet (når modtager åbner linket)
 */
export async function markCardAsOpened(shortCode) {
  try {
    const cardRef = doc(db, 'cards', shortCode)
    const cardDoc = await getDoc(cardRef)
    
    if (!cardDoc.exists()) {
      return { success: false, error: 'Card not found' }
    }
    
    const currentStatus = cardDoc.data().status
    
    // Opdater kun hvis status er sender_responded eller created
    if (currentStatus === CARD_STATUS.SENDER_RESPONDED || currentStatus === CARD_STATUS.CREATED) {
      await updateCardStatus(shortCode, CARD_STATUS.OPENED)
      return { success: true }
    }
    
    return { success: true } // Allerede åbnet eller anden status
  } catch (error) {
    console.error('❌ Error marking card as opened:', error)
    return { success: false, error: error.message }
  }
}
